import { Fragment } from 'react';
import {
  Banner,
  Icon,
  InlineStack,
  Badge,
  Link,
  Panel,
  PanelHeader,
  PanelInset,
  PanelSection,
  PanelTitle,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
  Tooltip,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import {
  getVisibleWarehouseOptions,
  getWarehouseDestinationName,
  isWarehouseSyncPausedForDisplay,
  WarehouseDataOptionID,
  type WarehouseDataOption,
  type WarehouseDataOptionTerms,
} from '@/features/warehouse-sync';
import type { WarehouseDestination, WarehouseSync } from '@/services/db/warehouse-sync/entity';
import { DetailsRow } from './details-row';
import * as styles from './details.css';

const CHANGELOG_URL = 'https://docs.customer.io/integrations/data-out/data-warehouses/data-warehouses-intro/#changelog';

const SYNC_INTERVAL_HINT =
  'We attempt to export parquet files every 15 minutes, though actual intervals and processing times may vary and this feature does not sync data in real time.';

/** Which options the schema upgrade turned off, so their rows can explain why. */
export type SchemaUpgradeAlertState = {
  people: boolean;
  outputs: boolean;
  subjects: boolean;
};

type DetailsProps = {
  sync: WarehouseSync;
  destination: WarehouseDestination;
  isReadOnly: boolean;
  isTwilioEnabled: boolean;
  terms: WarehouseDataOptionTerms;
  upgradeAlerts: SchemaUpgradeAlertState;
  onToggleOption: (optionId: WarehouseDataOption['id'], nextValue: boolean) => void;
  onUpgradeSchema: () => void;
};

function alertFor(optionId: WarehouseDataOption['id'], alerts: SchemaUpgradeAlertState): boolean {
  if (optionId === WarehouseDataOptionID.people) return alerts.people;
  if (optionId === WarehouseDataOptionID.outputs) return alerts.outputs;
  if (optionId === WarehouseDataOptionID.subjects) return alerts.subjects;
  return false;
}

/** The export-settings panel: sync status, schema version, and a row per export. */
export function Details({
  sync,
  destination,
  isReadOnly,
  isTwilioEnabled,
  terms,
  upgradeAlerts,
  onToggleOption,
  onUpgradeSchema,
}: DetailsProps) {
  const visibleOptions = getVisibleWarehouseOptions({ version: sync.version, isTwilioEnabled, terms });
  const hasError = !isEmptyString(sync.error);
  const isPaused = isWarehouseSyncPausedForDisplay(sync, visibleOptions);

  return (
    <Panel data-test-warehouse-details>
      <PanelHeader>
        <PanelTitle level="3" flexGrow="1">
          <InlineStack alignInline="space-between" mb="100">
            <InlineStack gap="100" alignBlock="center">
              <span data-test-warehouse-sync-detail-destination>
                Sync to {getWarehouseDestinationName(destination)}
              </span>
              {/* A failed sync reads Failed regardless of its toggles. */}
              {hasError ? (
                <Badge data-test-warehouse-sync-detail-status showStatusIndicator color="red">
                  Failed
                </Badge>
              ) : (
                <Badge data-test-warehouse-sync-detail-status showStatusIndicator color={isPaused ? 'grey' : 'green'}>
                  {isPaused ? 'Paused' : 'Enabled'}
                </Badge>
              )}
            </InlineStack>

            <Text text="product-pxs" color="subtle" data-test-warehouse-sync-detail-version>
              Export schema version: {sync.version}
            </Text>
          </InlineStack>
        </PanelTitle>
      </PanelHeader>

      <PanelSection>
        <PanelInset>
          <Table caption="Export settings">
            <TableThead>
              <TableTr>
                <TableTh className={styles.exportTypeColumn}>Export type</TableTh>
                <TableTh className={styles.lastSyncedColumn}>
                  <Tooltip content={SYNC_INTERVAL_HINT} placement="bottom">
                    <InlineStack display="inline-flex" gap="025" alignBlock="center">
                      Last synced
                      <Icon name="help" size="sm" color="subtle" />
                    </InlineStack>
                  </Tooltip>
                </TableTh>
                <TableTh textAlign="right" className={styles.exportToggleColumn}>
                  Export
                </TableTh>
              </TableTr>
            </TableThead>

            <TableTbody>
              {visibleOptions.map((option) => (
                <Fragment key={option.id}>
                  <DetailsRow
                    sync={sync}
                    option={option}
                    isActive={sync[option.id] === true}
                    isReadOnly={isReadOnly}
                    onToggle={onToggleOption}
                    onUpgradeSchema={onUpgradeSchema}
                  />
                  {alertFor(option.id, upgradeAlerts) ? (
                    <TableTr data-test-warehouse-sync-upgrade-warning={option.id}>
                      <TableTd colSpan={3}>
                        <Banner variant="caution">
                          {option.name} sync was disabled when your sync crossed to schema version 4.{' '}
                          {option.id === WarehouseDataOptionID.people ? (
                            <>
                              You'll need to delete any previous {terms.peoplePlural} files from your storage and
                              re-enable.
                            </>
                          ) : (
                            <>
                              Find out more in{' '}
                              <Link href={CHANGELOG_URL} variant="secondary" isExternal={true}>
                                the changelog.
                              </Link>
                            </>
                          )}
                        </Banner>
                      </TableTd>
                    </TableTr>
                  ) : null}
                </Fragment>
              ))}
            </TableTbody>
          </Table>
        </PanelInset>
      </PanelSection>
    </Panel>
  );
}
